import {
  ASSESSMENTS,
  capitalizeFirstLetter,
  CoverageStageStatus,
  FieldMapValue,
  getCoverageLabel,
  getCoveragePath,
  getCoverageRequiresCallIn,
  getCoverageStatus,
  InsuranceCoverage,
  mergeData,
  NextAction,
  PatientCoverageId,
  PopulatedAssessment,
  User,
  USERS,
  USERS_ADMIN,
  WithId,
} from '@hb/shared'
import { FORM_ERROR, ValidationErrors } from 'final-form'
import { collection, doc, DocumentReference, updateDoc } from 'firebase/firestore'
import { set as nestedSet } from 'nested-property'
import { createContext, PropsWithChildren, useCallback, useContext, useMemo } from 'react'
import { db } from '../../../backend'
import { useApp } from '../../../contexts'
import { PopUpMessageContext } from '../../../contexts/PopUpMessage/PopUpMessageContext'
import { updateAssessmentAnswers } from '../../../hooks'
import { addMetadata } from '../../../utils'
import { CoverageStageProps } from './types'

export interface CoverageViewContextValue {
  coverageId: PatientCoverageId
  coverage: InsuranceCoverage | undefined
  isMedicaid: boolean
  optional?: boolean
  propPath: string
  // patientId: string
  nextAction: NextAction | null | undefined
  patientRef: DocumentReference<User>
  assessmentId: string | null
  assessment: PopulatedAssessment | null
  status: CoverageStageStatus
  complete: boolean
  // adminPatientRef: DocumentReference<AdminUserData>
  isNew?: boolean
  handleSubmit: (data: FieldMapValue) => Promise<ValidationErrors>
  baseStoragePath: string
  adminView?: boolean
}

export const CoverageViewContext = createContext<CoverageViewContextValue>({
  // assessmentId: '',
  coverageId: 'primary',
  baseStoragePath: '',
  status: { incomplete: [], required: [] },
  complete: false,
  // patientId: '',
  nextAction: undefined,
  assessment: null,
  patientRef: doc(db, USERS, '__ERROR__') as DocumentReference<User>,
  // adminPatientRef: doc(db, USERS_ADMIN, '__ERROR__') as DocumentReference<AdminUserData>,
  coverage: undefined,
  handleSubmit: async () => ({}),
  isMedicaid: false,
  assessmentId: '',
  propPath: '',
})

const getAssessmentCoverageDataPath = (
  assessment: PopulatedAssessment | null,
  coverageId: PatientCoverageId,
) => {
  if (!assessment) return null
  if (assessment.primaryCoverageId === coverageId) return 'insuranceInfo.primaryCoverage'
  if (assessment.secondaryCoverageId === coverageId) return 'insuranceInfo.secondaryCoverage'

  return null
}

export const CoverageViewProvider = ({
  children,
  coverage,
  patientRef,
  id,
  nextAction,
  assessment,
  adminView,
  assessmentId,
  request,
}: PropsWithChildren<CoverageStageProps>) => {
  const { showMessage } = useContext(PopUpMessageContext)

  const coveragePath = useMemo(() => getCoveragePath(id), [id])
  const baseStoragePath = useMemo(() => {
    const coverageStoragePath = coveragePath.replace(/\./g, '/')
    return assessmentId
      ? `${ASSESSMENTS}/${assessmentId}/corrections/${coverageStoragePath}`
      : `${patientRef.path}/${coverageStoragePath}`
  }, [patientRef, coveragePath, assessmentId])
  const propPath = useMemo(() => getCoveragePath(id), [id])

  const requiresCallIn = useMemo(
    () => getCoverageRequiresCallIn(coverage, request),
    [coverage, request],
  )

  const status = useMemo(
    () => getCoverageStatus(coverage, requiresCallIn, request),
    [coverage, requiresCallIn, request],
  )
  const complete = useMemo(() => !status?.incomplete?.length, [status])

  const adminPatientRef = useMemo(
    () => doc(collection(db, USERS_ADMIN), patientRef.id),
    [patientRef],
  )

  const { appName } = useApp()

  const handleSubmit = useCallback(
    async (data: FieldMapValue) => {
      if (!patientRef) {
        showMessage({
          text: 'An error occurred',
          subText:
            'Please send us a message on the contact page and we will get back to you as soon as possible',
          type: 'error',
        })
        return { [FORM_ERROR]: 'An error occurred' }
      }
      try {
        const withMetadata = addMetadata(data, appName, !coverage?.createdOn)
        if (request && !coverage?.fromRequest) withMetadata.fromRequest = request
        if (request?.withCallInForm) {
          withMetadata.callInRequests = {
            [Date.now()]: addMetadata(
              {
                on: Date.now(),
                by: 'admin',
                notes: `From coverage request | ${request.message}`,
              },
              appName,
              true,
            ),
            ...withMetadata.callInRequests,
          }
        }
        const coverageDataPath = getAssessmentCoverageDataPath(assessment ?? null, id)
        if (assessmentId && coverageDataPath) {
          const submitted = {}
          nestedSet(submitted, coverageDataPath, withMetadata)
          await updateAssessmentAnswers({
            id: assessmentId,
            data: submitted,
            appName,
          })
        } else {
          await updateDoc(patientRef, propPath, mergeData(coverage ?? {}, withMetadata))
        }
        showMessage({
          type: 'success',
          text: `${capitalizeFirstLetter(getCoverageLabel(id))} coverage updated!`,
        })
      } catch (err: any) {
        showMessage({
          text: 'An error occurred',
          subText:
            err.message ||
            'Please send us a message on the contact page and we will get back to you as soon as possible',
          type: 'error',
        })
        return {
          [FORM_ERROR]:
            err.message ||
            'An error occurred. Please send us a message on the contact page and we will get back to you as soon as possible',
        }
      }
      return undefined
    },
    [patientRef, showMessage, propPath, coverage, appName, request, id, assessmentId, assessment],
  )

  // const isMedicaid = useMemo(() => {
  //   // if is primary coverage, check if the assessment is medicaid
  //   const fromCoverage = coverage?.isMedicaid
  //   if (fromCoverage !== null && fromCoverage !== undefined) return fromCoverage
  //   if (!assessment) return false
  //   if (assessment.primaryCoverageId === id)
  //     return !!assessment.data?.insuranceInfo?.primaryIsMedicaid
  //   if (assessment.secondaryCoverageId === id)
  //     return !!assessment.data?.insuranceInfo?.secondaryIsMedicaid
  //   return false
  // }, [assessment, id, coverage])

  const contextData = useMemo<CoverageViewContextValue>(
    () => ({
      coverage: coverage as WithId<InsuranceCoverage> | undefined,
      coverageId: id,
      isMedicaid: !!coverage?.isMedicaid,
      propPath,
      assessmentId,
      assessment: assessment ?? null,
      adminPatientRef,
      patientRef,
      nextAction,
      baseStoragePath,
      adminView,
      handleSubmit,
      status,
      complete,
    }),
    [
      coverage,
      propPath,
      adminView,
      handleSubmit,
      baseStoragePath,
      assessment,
      id,
      patientRef,
      adminPatientRef,
      assessmentId,
      nextAction,
      status,
      complete,
    ],
  )

  return <CoverageViewContext.Provider value={contextData}>{children}</CoverageViewContext.Provider>
}

export const useCoverageView = () => useContext(CoverageViewContext)
